import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ContestService, ContestTier, CONTEST_INTENT_KEY } from '../services/ContestService';
import { SingAlongService } from '../services/SingAlongService';
import { trackInitiateCheckout } from '../lib/analytics';
import { Mic, Crown, Zap, Building2, Check, Loader as Loader2, ArrowRight } from 'lucide-react';

const tierIcons: Record<string, React.ReactNode> = {
  individual: <Mic size={28} />,
  corporate: <Building2 size={28} />,
  sponsored: <Crown size={28} />,
  subscription: <Zap size={28} />,
};

export const ContestPlans: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tiers, setTiers] = useState<ContestTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canceled = searchParams.get('canceled') === 'true';

  const startCheckout = async (tierId: string, tierName?: string, priceCents?: number) => {
    setError(null);
    setPurchasing(tierId);
    if (tierName && priceCents) {
      trackInitiateCheckout(tierName, priceCents);
    }
    try {
      const url = await ContestService.createCheckout(tierId);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Checkout failed');
      setPurchasing(null);
    }
  };

  useEffect(() => {
    (async () => {
      const user = await SingAlongService.getCurrentUser();
      if (!user || !user.email_confirmed_at) {
        navigate('/sing-along/signup', { replace: true });
        return;
      }

      try {
        const [tierData, purchase] = await Promise.all([
          ContestService.getTiers(),
          SingAlongService.getActivePurchase(),
        ]);
        setTiers(tierData);

        if (purchase) {
          const hasSub = await ContestService.hasActiveSubscription();
          if (hasSub || purchase.league_id) {
            navigate('/contests/dashboard', { replace: true });
          } else {
            navigate('/contests/setup', { replace: true });
          }
          return;
        }

        const intentSlug = sessionStorage.getItem(CONTEST_INTENT_KEY);
        if (intentSlug) {
          sessionStorage.removeItem(CONTEST_INTENT_KEY);
          const intended = tierData.find((t) => t.slug === intentSlug);
          if (intended) {
            await startCheckout(intended.id, intended.name, intended.price_cents);
            return;
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load plans');
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white px-4 py-16">
      <div className="absolute inset-0 bg-gradient-to-br from-sky-600/10 via-transparent to-emerald-600/10 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <Link to="/contests" className="inline-flex items-center gap-2 text-white/40 hover:text-white/70 text-sm mb-6 transition">
            <img src="/TSL_Logo.png" alt="The Singing League" className="h-7" />
          </Link>
          <h1 className="text-3xl sm:text-4xl font-black mb-3">Choose Your Contest Plan</h1>
          <p className="text-white/50 text-lg">Pick the tier that fits your event and launch in minutes</p>
        </div>

        {canceled && (
          <div className="max-w-xl mx-auto mb-8 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-center text-sm">
            Checkout was canceled. You can pick a plan again whenever you're ready.
          </div>
        )}

        {error && (
          <div className="max-w-xl mx-auto mb-8 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-center text-sm">
            {error}
          </div>
        )}

        {tiers.length === 0 && !error ? (
          <div className="text-center py-16 text-white/40">
            <p className="text-lg">No plans available at the moment. Check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
            {tiers.map((tier) => (
              <div
                key={tier.id}
                className={`relative flex flex-col rounded-2xl border transition-all duration-200 overflow-hidden ${
                  tier.highlighted
                    ? 'border-emerald-500/50 bg-emerald-500/5 shadow-lg shadow-emerald-500/10'
                    : 'border-white/10 bg-white/[0.03] hover:border-white/20'
                }`}
              >
                {tier.badge && (
                  <div
                    className="absolute top-0 right-0 px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-bl-lg"
                    style={{ backgroundColor: tier.accent_color, color: '#0f172a' }}
                  >
                    {tier.badge}
                  </div>
                )}
                <div className="p-6 flex-1">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${tier.accent_color}20`, color: tier.accent_color }}
                  >
                    {tierIcons[tier.slug] ?? <Mic size={28} />}
                  </div>
                  <h3 className="font-bold text-lg mb-1">{tier.name}</h3>
                  <p className="text-white/50 text-sm mb-5 leading-relaxed">{tier.description}</p>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-3xl font-black">${(tier.price_cents / 100).toFixed(2)}</span>
                    {tier.period_label && <span className="text-white/40 text-sm">{tier.period_label}</span>}
                  </div>
                  <ul className="space-y-2.5">
                    {tier.features.map((feat, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm">
                        <Check size={15} className="mt-0.5 shrink-0" style={{ color: tier.accent_color }} />
                        <span className="text-white/75">{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="p-6 pt-0">
                  <button
                    onClick={() => startCheckout(tier.id, tier.name, tier.price_cents)}
                    disabled={!!purchasing}
                    className="w-full py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ backgroundColor: tier.accent_color, color: '#0f172a' }}
                  >
                    {purchasing === tier.id ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <>
                        {tier.billing_mode === 'subscription' ? 'Subscribe' : 'Get Started'}
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContestPlans;
